import { ApolloError } from '@apollo/client'
import { useState } from 'react'
import { useRateQueryForResourceLazyQuery } from 'src/api/api.generated'
import { isDateBetween, getNightsInRange, compareDates } from 'src/utils/date'

export function useReservedResourceDailyPricesUtils(tenantId: string) {
  const [reservationRatesQuery, { loading }] = useRateQueryForResourceLazyQuery()

  const [errors, setErrors] = useState<ApolloError | undefined>()

  const getUpdatedDailyPrices = async (
    propertyId: string,
    resourceTypeId: string,
    rateId: string,
    start: Date,
    end: Date,
    originalRRStart: Date,
    originalRREnd: Date,
    chargeCategoryId: string,
    adults: number,
    childOccupancy?: { childId: string; quantity: number }[],
    manualPrice?: number | null,
    manualDailyPrices?: { day: Date; price: number }[] | null,
    dailyPricesStored?: { day: Date; price: number }[] | null,
  ) => {
    let resultDailyPrices: { day: Date; price: number }[] = []
    let currentFilteredDailyPrices: { day: Date; price: number }[] = []

    let originalRange: Date[] = []
    if (manualPrice) {
      currentFilteredDailyPrices = getNightsInRange(originalRRStart, originalRREnd)?.map((d) => ({ day: d, price: manualPrice }))
    } else if (manualDailyPrices) {
      originalRange = manualDailyPrices.map(({ day }) => day)
      currentFilteredDailyPrices = manualDailyPrices.filter((dp) => isDateBetween(dp.day, start, end, false, true))
    } else if (dailyPricesStored) {
      originalRange = dailyPricesStored.map(({ day }) => day)
      currentFilteredDailyPrices = dailyPricesStored.filter((dp) => isDateBetween(dp.day, start, end, false, true))
    }

    if (originalRange.find((d) => compareDates(start, d) === 0) && originalRange.find((d) => compareDates(end, d) === 0)) {
      if (manualDailyPrices || dailyPricesStored) resultDailyPrices = currentFilteredDailyPrices
    } else {
      const result = await reservationRatesQuery({
        variables: {
          filter: {
            tenantId: tenantId,
            propertyId: propertyId,
            start: start,
            end: end,
            resourceTypeId: resourceTypeId,
            rateId: rateId,
            chargeCategoryId: chargeCategoryId,
            adults: adults,
            childOccupancy: childOccupancy,
          },
        },
      })

      if (result.data && !result.error) {
        // Merge with original daily prices
        resultDailyPrices =
          result.data.rateQueryForResource.rate.resourceRates?.[0].dailyPrice.map((dp) => {
            return { day: dp.day, price: currentFilteredDailyPrices.find(({ day }) => compareDates(dp.day, day) === 0)?.price ?? dp.price }
          }) ?? []
      } else {
        if (result.error) setErrors(result.error)
        return
      }
    }

    return resultDailyPrices
  }

  return { getUpdatedDailyPrices, loading, errors }
}
